import type { ColorScheme, ThemeType } from '@/types'

/**
 * Color scheme definitions
 */
export const COLOR_SCHEMES: Record<
  ColorScheme,
  {
    primary: string
    primaryLight: string
    primaryDark: string
  }
> = {
  blue: {
    primary: '#3B82F6',
    primaryLight: '#DBEAFE',
    primaryDark: '#1E40AF',
  },
  purple: {
    primary: '#8B5CF6',
    primaryLight: '#EDE9FE',
    primaryDark: '#5B21B6',
  },
  green: {
    primary: '#10B981',
    primaryLight: '#D1FAE5',
    primaryDark: '#047857',
  },
  orange: {
    primary: '#F59E0B',
    primaryLight: '#FEF3C7',
    primaryDark: '#B45309',
  },
  dark: {
    primary: '#1F2937',
    primaryLight: '#F3F4F6',
    primaryDark: '#111827',
  },
  red: {
    primary: '#EF4444',
    primaryLight: '#FEE2E2',
    primaryDark: '#B91C1C',
  },
  teal: {
    primary: '#14B8A6',
    primaryLight: '#CCFBF1',
    primaryDark: '#0F766E',
  },
  indigo: {
    primary: '#6366F1',
    primaryLight: '#E0E7FF',
    primaryDark: '#4338CA',
  },
  rose: {
    primary: '#F43F5E',
    primaryLight: '#FFE4E6',
    primaryDark: '#BE123C',
  },
  emerald: {
    primary: '#059669',
    primaryLight: '#D1FAE5',
    primaryDark: '#065F46',
  },
}

/**
 * Theme metadata for display and selection
 */
export const THEME_METADATA: Record<
  ThemeType,
  {
    name: string
    description: string
    features: string[]
  }
> = {
  modern: {
    name: 'Modern',
    description: 'Apple-style liquid glass with floating blur effects and soft gradients',
    features: [
      'Frosted glass panels (backdrop-blur-2xl)',
      'Animated floating gradient blobs',
      'Soft pastel background gradients',
      'Rounded-[2.5rem] ultra-smooth corners',
    ],
  },
  classic: {
    name: 'Classic',
    description: 'Traditional print-style resume with serif fonts and formal layout',
    features: [
      'Serif typography throughout',
      'Single-page paper aesthetic',
      'Uppercase section headers with underlines',
      'Print-ready professional format',
    ],
  },
  minimal: {
    name: 'Minimal',
    description: 'Ultra-clean design with massive typography and generous whitespace',
    features: [
      'Huge font-light headings (6xl-8xl)',
      'Monochrome gray color palette',
      'Extra generous spacing (mb-24)',
      'Tiny uppercase section labels',
    ],
  },
  creative: {
    name: 'Creative',
    description: 'Bold magazine-style with rotated text, thick borders, and vibrant colors',
    features: [
      'Rotated typography and polygon shapes',
      'Border-4/8 black outlines',
      'Colorful gradients and yellow accents',
      'Magazine/poster aesthetic',
    ],
  },
}

/**
 * Color scheme metadata for display
 */
export const COLOR_SCHEME_METADATA: Record<
  ColorScheme,
  {
    name: string
    description: string
  }
> = {
  blue: {
    name: 'Blue',
    description: 'Professional and trustworthy',
  },
  purple: {
    name: 'Purple',
    description: 'Creative and innovative',
  },
  green: {
    name: 'Green',
    description: 'Fresh and growth-oriented',
  },
  orange: {
    name: 'Orange',
    description: 'Energetic and enthusiastic',
  },
  dark: {
    name: 'Dark',
    description: 'Bold and modern',
  },
  red: {
    name: 'Red',
    description: 'Passionate and powerful',
  },
  teal: {
    name: 'Teal',
    description: 'Calm and sophisticated',
  },
  indigo: {
    name: 'Indigo',
    description: 'Deep and confident',
  },
  rose: {
    name: 'Rose',
    description: 'Elegant and warm',
  },
  emerald: {
    name: 'Emerald',
    description: 'Vibrant and balanced',
  },
}
